import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Inject,
  PLATFORM_ID,
  Renderer2,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';

import { ImageLoadingComponent } from '@common/image-loading/image-loading.component';
import { CommonGuideComponent } from '../common-guide.component';
import { PreferenceKey } from '@helper/preference-key';
import { BubbleContentComponent } from '../bubble-content/bubble-content.component';
import { ContentArrowPosition } from '../bubble-content/content-arrow-position';
import { WindowDetectService } from '@service/window-detect.service';
import { CartDialogModel } from '../../cart-dialog/cart-dialog-model';

enum Step {
  FIRST,
  SECOND,
}

interface IDialogData {
  cartBottomSheetHeight: number;
}

@Component({
  selector: 'app-cart-dialog-guide',
  templateUrl: './cart-dialog-guide.component.html',
  styleUrls: ['./cart-dialog-guide.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ImageLoadingComponent, MatDialogModule, BubbleContentComponent],
})
export class CartDialogGuideComponent extends CommonGuideComponent {
  override get key(): string {
    return PreferenceKey.GUIDE_CART_DIALOG;
  }

  readonly data: IDialogData = inject(MAT_DIALOG_DATA);
  readonly defaultPadding = 8;
  readonly arrowPosition = ContentArrowPosition;
  readonly content = $localize`點擊切換圖片模式`;
  readonly step = Step;
  readonly stepIndex = signal(Step.FIRST);
  readonly topDistance = computed(() => {
    try {
      const sab: string = getComputedStyle(this.docu.documentElement).getPropertyValue('--sab').trim();
      const value = Number(sab.replace('px', '')) || 0;
      return this.windowDetectService.windowHeight() - this.data.cartBottomSheetHeight - value;
    } catch {
      return this.windowDetectService.windowHeight() - this.data.cartBottomSheetHeight;
    }
  });
  readonly rightDistance = computed(() =>
    this.windowDetectService.isTablet()
      ? (this.windowDetectService.windowWidth() - 400) / 2 + this.defaultPadding
      : this.defaultPadding
  );

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private windowDetectService: WindowDetectService,
    public dialogRef: MatDialogRef<CartDialogGuideComponent>,
    public viewModel: CartDialogModel,
    @Inject(PLATFORM_ID) platformId: object,
    @Inject(DOCUMENT) private docu: Document,
    renderer: Renderer2
  ) {
    super(platformId, docu, renderer);
    afterNextRender(() => {
      this.dialogRef
        .backdropClick()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.nextStep());
    });
  }

  static open(dialog: MatDialog, cartBottomSheetHeight: number): MatDialogRef<CartDialogGuideComponent> {
    const data: IDialogData = { cartBottomSheetHeight: cartBottomSheetHeight };
    return dialog.open(CartDialogGuideComponent, {
      panelClass: 'guide-dialog-panel',
      disableClose: false,
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data,
    });
  }

  handleBackdropClose(): void {
    this.hideGuide();
    this.dialogRef.close();
  }

  nextStep(): void {
    if (this.stepIndex() < Step.SECOND) {
      this.stepIndex.update(index => index + 1);
    } else {
      this.handleBackdropClose();
    }
  }
}
